import { ROLES_MANAGE } from './capabilities';
import { DIVE_EMBED_MANAGE } from './dive-embed-permissions';
import { PRICE_BAND_RUN_MANAGE } from './price-band-run-permissions';
import { PRICING_ACTION_ENABLE } from './pricing-action-permissions';
import { MEMBERSHIPS_ADMIN_KEYS } from './tenant-memberships-admin.guard';
import { UI_CONFIG_MANAGE } from './ui-config-admin.guard';
import { USER_PERMISSIONS_MANAGE } from './user-permissions.constants';
import { USER_TYPES_MANAGE } from './user-types.constants';

/** A staged rollout a tool waits on beyond its permission. */
export type AdminToolRollout = 'weekly-digest';

export interface AdminToolAccessRule {
  readonly route: string;
  /** Any-of; empty only for CMI configuration, which `configurationNav` opens instead. */
  readonly permissionKeys: readonly string[];
  readonly rollout?: AdminToolRollout;
  readonly configurationNav?: true;
}

export interface AdminToolGates {
  readonly granted: ReadonlySet<string>;
  readonly weeklyDigest: boolean;
  /** The configuration flag is on and the caller is privileged. */
  readonly configuration: boolean;
  readonly pricingActionOnly: boolean;
}

const deepFrozen = (rules: readonly AdminToolAccessRule[]): readonly AdminToolAccessRule[] =>
  Object.freeze(
    rules.map((rule) =>
      Object.freeze({ ...rule, permissionKeys: Object.freeze([...rule.permissionKeys]) })
    )
  );

/** Every admin tool and what opens it, in rail order: the rail and the `/admin` guard share it. */
export const ADMIN_TOOL_ACCESS = deepFrozen([
  { route: '/admin/configs/column-mapping', permissionKeys: ['column_mapping.manage'] },
  { route: '/admin/configs/ui', permissionKeys: [UI_CONFIG_MANAGE] },
  { route: '/admin/configuration', permissionKeys: [], configurationNav: true },
  { route: '/admin/memberships', permissionKeys: MEMBERSHIPS_ADMIN_KEYS },
  { route: '/admin/access-roles', permissionKeys: [ROLES_MANAGE] },
  { route: '/admin/data-scopes', permissionKeys: ['data_scope.manage'] },
  { route: '/admin/access-audit', permissionKeys: [ROLES_MANAGE] },
  { route: '/admin/pricing-action', permissionKeys: [PRICING_ACTION_ENABLE] },
  { route: '/admin/price-band-runs', permissionKeys: [PRICE_BAND_RUN_MANAGE] },
  { route: '/admin/dive-publishing', permissionKeys: [DIVE_EMBED_MANAGE] },
  {
    route: '/admin/email-subscriptions',
    permissionKeys: ['email_subscriptions.manage'],
    rollout: 'weekly-digest',
  },
  { route: '/admin/permissions', permissionKeys: [USER_PERMISSIONS_MANAGE] },
  { route: '/admin/user-types', permissionKeys: [USER_TYPES_MANAGE] },
]);

/** The keys that open at least one admin tool. */
export const ADMIN_TOOL_PERMISSION_KEYS: readonly string[] = Object.freeze([
  ...new Set(ADMIN_TOOL_ACCESS.flatMap((rule) => rule.permissionKeys)),
]);

/** The routes of the tools these gates open, in rail order. */
export function visibleAdminRoutes(gates: AdminToolGates): readonly string[] {
  return ADMIN_TOOL_ACCESS.filter(
    (rule) => isHeld(rule, gates) && isRolledOut(rule.rollout, gates)
  ).map((rule) => rule.route);
}

function isHeld(rule: AdminToolAccessRule, gates: AdminToolGates): boolean {
  return rule.configurationNav
    ? gates.configuration && !gates.pricingActionOnly
    : rule.permissionKeys.some((key) => gates.granted.has(key));
}

function isRolledOut(rollout: AdminToolRollout | undefined, gates: AdminToolGates): boolean {
  switch (rollout) {
    case undefined:
      return true;
    case 'weekly-digest':
      return gates.weeklyDigest;
    default: {
      const _: never = rollout;
      return _;
    }
  }
}
